import type { QuoteRow } from '../../lib/types';

export const QUOTE_INTEGRITY_MESSAGE =
  'This quote does not pass the pricing integrity check. Ask a dispatcher to create a new quote version before approval.';

export function quoteOriginLabel(distanceSource: string) {
  if (distanceSource === 'reviewed_manual') return 'Reviewed manual quote';
  if (distanceSource === 'legacy_flat') return 'Legacy reviewed quote';
  return 'Automatic quote ready';
}

type QuoteAmounts = Pick<
  QuoteRow,
  | 'id'
  | 'version'
  | 'estimated_item_cost_centavos'
  | 'approved_spending_limit_centavos'
  | 'base_delivery_fee_centavos'
  | 'extra_distance_charge_centavos'
  | 'load_surcharge_centavos'
  | 'delivery_fee_centavos'
  | 'service_fee_centavos'
  | 'platform_commission_centavos'
  | 'rider_net_earnings_centavos'
  | 'total_centavos'
>;

export interface QuoteSnapshot {
  quoteId: string;
  version: number;
  itemEstimateCentavos: number;
  purchaseBudgetCentavos: number;
  baseDeliveryFeeCentavos: number;
  extraDistanceChargeCentavos: number;
  loadSurchargeCentavos: number;
  totalDeliveryFeeCentavos: number;
  serviceFeeCentavos: number;
  totalQuotedDueCentavos: number;
  platformCommissionCentavos: number;
  riderNetEarningsCentavos: number;
  valid: boolean;
}

export function createQuoteSnapshot(quote: QuoteAmounts): QuoteSnapshot {
  const monetaryValues = [
    quote.estimated_item_cost_centavos,
    quote.approved_spending_limit_centavos,
    quote.base_delivery_fee_centavos,
    quote.extra_distance_charge_centavos,
    quote.load_surcharge_centavos,
    quote.delivery_fee_centavos,
    quote.service_fee_centavos,
    quote.platform_commission_centavos,
    quote.rider_net_earnings_centavos,
    quote.total_centavos,
  ];
  const valuesAreValid = monetaryValues.every((value) => Number.isSafeInteger(value) && value >= 0);
  const calculatedDelivery = quote.base_delivery_fee_centavos
    + quote.extra_distance_charge_centavos
    + quote.load_surcharge_centavos;
  const calculatedTotal = quote.estimated_item_cost_centavos
    + quote.delivery_fee_centavos
    + quote.service_fee_centavos;
  const calculatedRiderNet = quote.delivery_fee_centavos - quote.platform_commission_centavos;

  return {
    quoteId: quote.id,
    version: quote.version,
    itemEstimateCentavos: quote.estimated_item_cost_centavos,
    purchaseBudgetCentavos: quote.approved_spending_limit_centavos,
    baseDeliveryFeeCentavos: quote.base_delivery_fee_centavos,
    extraDistanceChargeCentavos: quote.extra_distance_charge_centavos,
    loadSurchargeCentavos: quote.load_surcharge_centavos,
    totalDeliveryFeeCentavos: quote.delivery_fee_centavos,
    serviceFeeCentavos: quote.service_fee_centavos,
    totalQuotedDueCentavos: quote.total_centavos,
    platformCommissionCentavos: quote.platform_commission_centavos,
    riderNetEarningsCentavos: quote.rider_net_earnings_centavos,
    valid: valuesAreValid
      && quote.delivery_fee_centavos === calculatedDelivery
      && quote.total_centavos === calculatedTotal
      && quote.rider_net_earnings_centavos === calculatedRiderNet,
  };
}

export function quoteCanBeApproved(currentQuoteId: string | null, snapshot: QuoteSnapshot): boolean {
  return snapshot.valid && currentQuoteId === snapshot.quoteId;
}
